import React, { useEffect, useState, useCallback } from "react";
import { Inbox, LogOut, Loader2, Lock, Mail, Building2, Tag } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const opts = { credentials: "include" };

const inputCls =
  "w-full border border-[#00205B]/30 bg-white px-4 py-3 text-sm nnc-text placeholder:text-[#00205B]/40 focus:outline-none focus:border-[#FCB814] focus:ring-1 focus:ring-[#FCB814] transition-colors";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadInquiries = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/api/admin/inquiries`, opts);
    if (res.ok) setInquiries(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => {
    (async () => {
      let res = await fetch(`${API}/api/auth/me`, opts);
      if (res.status === 401) {
        const r = await fetch(`${API}/api/auth/refresh`, { method: "POST", ...opts });
        if (r.ok) res = await fetch(`${API}/api/auth/me`, opts);
      }
      if (res.ok) {
        setUser(await res.json());
        loadInquiries();
      }
      setChecking(false);
    })();
  }, [loadInquiries]);

  const login = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        ...opts,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === "string" ? data.detail : "Login failed. Check your entries.");
      }
      setUser(data);
      loadInquiries();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: "POST", ...opts });
    setUser(null);
    setInquiries([]);
  };

  if (checking) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="h-8 w-8 animate-spin nnc-text" />
      </main>
    );
  }

  if (!user) {
    return (
      <main data-testid="admin-login-page">
        <section className="bg-white">
          <div className="max-w-md mx-auto px-6 py-24 lg:py-32">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="nnc-gold-rule" />
              <span className="nnc-overline">Owner Access</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight nnc-text">
              Inquiry Dashboard
            </h1>
            <p className="mt-4 text-sm leading-relaxed nnc-text">
              This page is private. Sign in with your owner credentials to
              view inquiries submitted through the website.
            </p>
            <form onSubmit={login} className="mt-10 border border-[#00205B]/15 bg-[#FBF2DE]/40 p-8" data-testid="admin-login-form">
              <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2">
                Email
              </label>
              <input
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                data-testid="admin-email-input"
              />
              <label className="block text-xs font-bold uppercase tracking-[0.14em] nnc-text mb-2 mt-5">
                Password
              </label>
              <input
                required
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                data-testid="admin-password-input"
              />
              {error && (
                <p className="mt-4 text-sm font-bold text-red-700" data-testid="admin-login-error">
                  {error}
                </p>
              )}
              <button type="submit" disabled={submitting} className="nnc-btn mt-8 w-full justify-center disabled:opacity-60" data-testid="admin-login-submit">
                {submitting ? (
                  <>
                    Signing in… <Loader2 className="h-4 w-4 animate-spin" />
                  </>
                ) : (
                  <>
                    Sign In <Lock className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main data-testid="admin-dashboard">
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <div className="flex flex-wrap items-start justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="nnc-gold-rule" />
                <span className="nnc-overline">Owner Dashboard</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight nnc-text">
                Website Inquiries
              </h1>
              <p className="mt-2 text-sm nnc-text" data-testid="admin-inquiry-count">
                {inquiries.length} {inquiries.length === 1 ? "inquiry" : "inquiries"} received
              </p>
            </div>
            <button onClick={logout} className="nnc-btn-outline inline-flex items-center gap-2 border border-[#00205B] px-5 py-2.5 text-sm font-bold nnc-text" data-testid="admin-logout-button">
              Sign Out <LogOut className="h-4 w-4" />
            </button>
          </div>

          <div className="mt-12">
            {loading ? (
              <div className="flex justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin nnc-text" />
              </div>
            ) : inquiries.length === 0 ? (
              <div className="border border-[#00205B]/20 px-10 py-16 text-center" data-testid="admin-empty-state">
                <Inbox className="h-9 w-9 nnc-gold-text mx-auto" />
                <p className="mt-4 text-sm font-bold nnc-text">No inquiries yet.</p>
                <p className="mt-2 text-sm nnc-text">
                  Submissions from the contact form will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-6" data-testid="admin-inquiry-list">
                {inquiries.map((q) => (
                  <article key={q.inquiry_id} className="border border-[#00205B]/15 bg-[#FBF2DE]/30 p-6 lg:p-8" data-testid="admin-inquiry-card">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <h2 className="text-lg font-bold nnc-text">{q.name}</h2>
                      <span className="text-xs font-bold uppercase tracking-wide nnc-gold-text">
                        {formatDate(q.created_at)}
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm nnc-text">
                      <span className="inline-flex items-center gap-2">
                        <Mail className="h-4 w-4 nnc-gold-text" />
                        <a href={`mailto:${q.email}`} className="underline underline-offset-2">{q.email}</a>
                      </span>
                      {q.organization && (
                        <span className="inline-flex items-center gap-2">
                          <Building2 className="h-4 w-4 nnc-gold-text" />
                          {q.organization}
                        </span>
                      )}
                      {q.service_interest && (
                        <span className="inline-flex items-center gap-2">
                          <Tag className="h-4 w-4 nnc-gold-text" />
                          {q.service_interest}
                        </span>
                      )}
                    </div>
                    <p className="mt-4 text-sm leading-relaxed nnc-text whitespace-pre-wrap border-l-2 border-[#FCB814] pl-4">
                      {q.message}
                    </p>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
